import React, { useState, useEffect, useCallback } from 'react';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { AppraisalsApi } from '@apply/api';
import { applicationInsights } from '../../../../ApplicationInsights';
import store, { appraiserActions } from '@apply/adminStore';
import AppraiserModal from '../../../../appraisals/appraisers/modals/AppraiserModal';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { formatPhone } from '../../../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faPencil } from '@fortawesome/free-solid-svg-icons';

export const AppraiserSelect = ({
    appraiserId,
    onSelect = null,
    disabled = false,
    required = true,
    canEdit = true,
    showHeader = true,
    id,
}) => {
    const PER_PAGE = 20;
    const api = new AppraisalsApi.AppraisalsApi(applicationInsights);
    const { dispatch } = store;

    //Borrower Search
    const [cache] = useState({});
    const [searchLoading, set_searchLoading] = useState(false);
    const [appraisers, set_appraisers] = useState([]);
    const [query, set_query] = useState('');
    const [appraiser, set_appraiser] = useState(null);
    const [appraiserDetail, set_appraiserDetail] = useState(null);

    const styles = {
        formRow: { marginBottom: '0' },
        formRowTypeAhead: { marginBottom: '0', width: '99%' },
    };

    useEffect(() => {
        async function appraiserIdChanged() {
            if (!appraiserId) {
                resetState();
                return;
            }

            const response = await api.getAppraiser(appraiserId);
            if (response.statusCode !== 200) {
                return;
            }
            const appr = response.result;
            set_appraiser({ key: appr.id, name: appr.displayName });
            set_appraiserDetail(appr);
        }

        appraiserIdChanged();
    }, [appraiserId]);

    const resetState = () => {
        set_appraisers([]);
        set_appraiser(null);
        set_appraiserDetail(null);
    };

    const addAppraiser = () => {
        dispatch(
            appraiserActions.setAppraiserModal({
                appraiserModal: {
                    isOpen: true,
                    appraiserId: 0,
                    onSave: onAfterAppraiserEdit,
                },
            })
        );
    };

    const editAppraiser = (id) => {
        dispatch(
            appraiserActions.setAppraiserModal({
                appraiserModal: {
                    isOpen: true,
                    appraiserId: id,
                    onSave: onAfterAppraiserEdit,
                },
            })
        );
    };

    const onAfterAppraiserEdit = async (id) => {
        if (!id) {
            set_appraiser(null);
            set_appraiserDetail(null);
            return;
        }

        const response = await api.getAppraiser(id);
        if (response.statusCode !== 200) {
            return;
        }

        const apprDetail = response.result;
        if (apprDetail) {
            set_appraiser({ key: apprDetail.id, name: apprDetail.displayName });
            set_appraiserDetail(apprDetail);
            onSelect && onSelect(apprDetail.id, cancelSelect);
        }
    };

    const cancelSelect = () => {
        set_appraiser(null);
    };

    const searchAppraisers = async (query, page = 1) => {
        const response = await api.search(query, page, PER_PAGE);
        if (response.statusCode !== 200) {
            return [];
        }
        return response.result;
    };

    const handleInputChange = (q) => {
        q = (q ?? '').toLowerCase();
        set_query(q);
    };

    const handleSearch = useCallback((q) => {
        q = (q ?? '').toLowerCase();
        if (cache[q]) {
            set_appraisers(cache[q].options);
            return;
        }

        set_searchLoading(true);
        searchAppraisers(q).then((resp) => {
            cache[q] = { options: resp, page: 1 };
            set_searchLoading(false);
            set_appraisers(resp);
        });
    }, []);

    const handlePagination = (e, shownResults) => {
        const cachedQuery = cache[query];

        // Don't make another request if:
        // - the cached results exceed the shown results
        // - we've already fetched all possible results
        if (cachedQuery.options.length > shownResults || cachedQuery.options.length === cachedQuery.total_count) {
            return;
        }

        set_searchLoading(true);

        const page = cachedQuery.page + 1;

        searchAppraisers(query, page).then((resp) => {
            const options = cachedQuery.options.concat(resp);
            cache[query] = { ...cachedQuery, options, page };
            set_searchLoading(false);
            set_appraisers(options);
        });
    };

    const setAppraiser = async (appr) => {
        set_appraiser(appr);
        if (!appr) {
            return;
        }
        const response = await api.getAppraiser(appr.key);
        if (response.statusCode !== 200) {
            return;
        }
        if (response.result) {
            set_appraiserDetail(response.result);
        }
        onSelect && onSelect(appr.key, cancelSelect);
    };

    const clearAppraiser = () => {
        set_appraiser(null);
        onSelect && onSelect(null, cancelSelect);
    };

    return (
        <>
            <div
                className='row'
                style={styles.formRowTypeAhead}
            >
                <div
                    className='columns small-12 large-12'
                    style={{ paddingBottom: '0' }}
                >
                    <div className='type-ahead-container'>
                        {showHeader && (
                            <label htmlFor='borrower-search'>Appraiser {required && <span className='req'>*</span>}</label>
                        )}
                        <AsyncTypeahead
                            className='type-ahead'
                            id={'appraiser-search' + id}
                            isLoading={searchLoading}
                            labelKey={(option) => `${option.name}`}
                            maxResults={PER_PAGE - 1}
                            minLength={2}
                            onInputChange={handleInputChange}
                            onPaginate={handlePagination}
                            onSearch={handleSearch}
                            onChange={(event) => setAppraiser(event[0])}
                            options={appraisers}
                            paginate
                            placeholder='Search for an Appraiser (at least 2 characters)...'
                            renderMenuItemChildren={(option) => (
                                <div key={option.key}>
                                    <span>{option.name}</span>
                                </div>
                            )}
                            useCache={false}
                            option
                            selected={appraiser ? [appraiser] : []}
                            disabled={disabled || appraiser}
                            inputProps={{ required: true }}
                        />
                        <button
                            type='button'
                            className='type-ahead-clear-button'
                            onClick={() => clearAppraiser()}
                            disabled={disabled || !appraiser}
                            title='Remove selected appraiser'
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        <button
                            type='button'
                            onClick={addAppraiser}
                            className='button type-ahead-add-button'
                            title='Add new Appraiser'
                            disabled={disabled}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                    </div>
                </div>
            </div>

            {appraiserDetail && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div className='columns small-12 large-4'>
                        <b>{appraiserDetail.companyName}</b>
                        <br />
                        {appraiserDetail.firstName} {appraiserDetail.lastName}
                        <br />
                        {appraiserDetail.email && (
                            <span>
                                {appraiserDetail.email}
                                <br />
                            </span>
                        )}
                        {appraiserDetail.phone && (
                            <span>
                                {formatPhone(appraiserDetail.phone)}
                                <br />
                            </span>
                        )}
                        {appraiserDetail.mailingAddress && (
                            <>
                                {appraiserDetail.mailingAddress.street1}
                                <br />
                                {appraiserDetail.mailingAddress.street2 && (
                                    <>
                                        {appraiserDetail.mailingAddress.street2}
                                        <br />
                                    </>
                                )}
                                {appraiserDetail.mailingAddress.city} {appraiserDetail.mailingAddress.state},{' '}
                                {appraiserDetail.mailingAddress.zipCode}
                            </>
                        )}
                    </div>
                    <div className='columns small-12 large-8'>
                        <button
                            type='button'
                            onClick={() => editAppraiser(appraiserDetail.id)}
                            className='button'
                            title='Edit Appraiser details'
                            style={{
                                verticalAlign: 'unset',
                                marginBottom: '0',
                            }}
                            disabled={!canEdit}
                        >
                            <FontAwesomeIcon icon={faPencil} /> Edit
                        </button>
                    </div>
                </div>
            )}
            {!appraiserDetail && (
                <div
                    className='row'
                    style={styles.formRow}
                >
                    <div className='columns small-12 large-12'>No appraiser selected...</div>
                </div>
            )}
            <AppraiserModal />
        </>
    );
};
